import { getCurrentSession } from './supabase'

const apiBase = import.meta.env.VITE_VERA_API_BASE_URL?.replace(/\/$/, '') || ''

async function authorizedFetch(path, options = {}) {
  if (!apiBase) throw new Error('Python API V2 chưa được cấu hình.')
  const session = await getCurrentSession()
  const headers = new Headers(options.headers || {})
  if (session?.access_token) headers.set('Authorization', `Bearer ${session.access_token}`)
  return fetch(`${apiBase}${path}`, { ...options, headers })
}

async function jsonRequest(path, options = {}) {
  const headers = new Headers(options.headers || {})
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  const response = await authorizedFetch(path, { ...options, headers })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(payload.detail || payload.message || `HTTP ${response.status}`)
  return payload
}

function identityKey(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd').replace(/Đ/g, 'D').toLocaleLowerCase('vi')
    .replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ')
}

async function validateDraftIdentity(media, fullName, cccdNumber) {
  if (!media?.portrait) throw new Error('Phải có ảnh nhân viên trước khi lưu.')
  if (!media?.front || !media?.back) throw new Error('Phải tải hoặc chụp đủ mặt trước và mặt sau CCCD trước khi lưu.')
  const results = await Promise.all([
    staffSecurityApi.extractIdentity(media.front),
    staffSecurityApi.extractIdentity(media.back),
  ])
  const extracted = {}
  results.forEach((result) => Object.entries(result.extracted_fields || {}).forEach(([key, value]) => {
    if (value && !extracted[key]) extracted[key] = value
  }))
  const declaredName = String(fullName || '').trim()
  const declaredNumber = String(cccdNumber || '').replace(/\D/g, '')
  if (!extracted.full_name) throw new Error('Không đọc rõ Họ và tên trên ảnh CCCD; vui lòng chụp hoặc tải lại ảnh rõ hơn.')
  if (!extracted.cccd_number) throw new Error('Không đọc rõ Số Căn cước trên ảnh CCCD; vui lòng chụp hoặc tải lại ảnh rõ hơn.')
  if (identityKey(extracted.full_name) !== identityKey(declaredName)) {
    throw new Error(`Họ và tên trên CCCD (${extracted.full_name}) không khớp với Họ và tên đã khai (${declaredName}).`)
  }
  if (String(extracted.cccd_number).replace(/\D/g, '') !== declaredNumber) {
    throw new Error(`Số Căn cước trên CCCD (${extracted.cccd_number}) không khớp với số đã khai (${declaredNumber}).`)
  }
  return extracted
}

async function downloadPdf(response, fallbackName) {
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}))
    throw new Error(payload.detail || payload.message || `HTTP ${response.status}`)
  }
  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition') || ''
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  const filename = encoded ? decodeURIComponent(encoded) : fallbackName
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

async function downloadArchive(path, fallbackName) {
  const response = await authorizedFetch(path)
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}))
    throw new Error(payload.detail || payload.message || `HTTP ${response.status}`)
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fallbackName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const staffSecurityApi = {
  validateDraftIdentity,
  resetPassword: (username, newPassword) => jsonRequest(`/v2/staff/${encodeURIComponent(username)}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ new_password: newPassword }),
  }),
  identityMetadata: (username) => jsonRequest(`/v2/staff/${encodeURIComponent(username)}/identity`),
  downloadAllPortraits: () => downloadArchive('/v2/staff/identity/export-portraits.zip', 'Anh_Nhan_Vien_Tat_Ca.zip'),
  extractIdentity: async (blob) => {
    const response = await authorizedFetch('/v2/staff/identity/ocr', {
      method: 'POST',
      headers: { 'Content-Type': blob.type || 'image/webp' },
      body: blob,
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(payload.detail || payload.message || `HTTP ${response.status}`)
    return payload
  },
  extractImageText: async (blob) => {
    const response = await authorizedFetch('/v2/staff/image-text', {
      method: 'POST',
      headers: { 'Content-Type': blob.type || 'image/webp' },
      body: blob,
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(payload.detail || payload.message || `HTTP ${response.status}`)
    return payload
  },
  identityBlob: async (username, side) => {
    const response = await authorizedFetch(`/v2/staff/${encodeURIComponent(username)}/identity/${encodeURIComponent(side)}`)
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      throw new Error(payload.detail || payload.message || `HTTP ${response.status}`)
    }
    return response.blob()
  },
  uploadIdentity: async (username, side, blob) => {
    const response = await authorizedFetch(`/v2/staff/${encodeURIComponent(username)}/identity/${encodeURIComponent(side)}`, {
      method: 'PUT',
      headers: { 'Content-Type': blob.type || 'image/webp' },
      body: blob,
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(payload.detail || payload.message || `HTTP ${response.status}`)
    return payload
  },
  deleteIdentity: (username, side) => jsonRequest(`/v2/staff/${encodeURIComponent(username)}/identity/${encodeURIComponent(side)}`, {
    method: 'DELETE',
  }),
  exportProfilePdf: async (username) => {
    const response = await authorizedFetch(`/v2/staff/${encodeURIComponent(username)}/profile.pdf`)
    await downloadPdf(response, `Ho_So_${username}.pdf`)
  },
  exportProfilesPdf: async (usernames) => {
    const response = await authorizedFetch('/v2/staff/profiles.pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ usernames }),
    })
    await downloadPdf(response, `Ho_So_Nhan_Vien_Da_Chon_${usernames.length}.pdf`)
  },
}


async function faceRequest(username, suffix = '', options = {}, binary = false) {
  const response = await authorizedFetch(`/v2/staff/${encodeURIComponent(username)}/face-id${suffix}`, options)
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}))
    const error = new Error(payload.detail || `HTTP ${response.status}`)
    error.status = response.status
    throw error
  }
  return binary ? response.blob() : response.json()
}

export const faceIdApi = {
  enrollment: (username) => faceRequest(username, '/enrollment'),
  enroll: (username, photoSha, confirmed = true) => faceRequest(username, '/enrollment', {
    method: 'POST', headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({photo_sha256: photoSha, confirmed}),
  }),
  verifyEnrollment: (username) => faceRequest(username, '/enrollment/verify', {method: 'POST'}),
  downloadAll: () => downloadArchive('/v2/face-id/export-all.zip', 'Face_ID_Tat_Ca_Nhan_Vien.zip'),
  assignmentEmployees: () => jsonRequest('/v2/face-id/assignment-employees'),
  assignCapturePhoto: (username, blob, expectedSha) => faceRequest(username, '/capture-photo', {
    method: 'PUT',
    headers: {'Content-Type': blob.type, ...(expectedSha ? {'If-Match': `"${expectedSha}"`} : {'If-None-Match': '*'})},
    body: blob,
  }),
  batchPlan: (filenames) => jsonRequest('/v2/face-id/batch-plan', {method: 'POST', body: JSON.stringify({filenames})}),
  uploadBatchPhoto: (row) => faceRequest(row.username, `/image?filename=${encodeURIComponent(row.filename)}`, {
    method: 'PUT', headers: {'Content-Type': row.blob.type, ...(row.existing_sha256 ? {'If-Match': `"${row.existing_sha256}"`} : {'If-None-Match': '*'})}, body: row.blob,
  }),
  metadata: (username) => faceRequest(username),
  identityBlob: (username) => faceRequest(username, '/image', {}, true),
  uploadIdentity: (username, _side, blob) => faceRequest(username, '/image', {method: 'PUT', headers: {'Content-Type': blob.type}, body: blob}),
  deleteIdentity: (username) => faceRequest(username, '/image', {method: 'DELETE'}),
  portrait: (username) => faceRequest(username, '/portrait-source', {}, true),
  captures: (username, day, offset = 0) => faceRequest(username, `/captures?day=${encodeURIComponent(day)}&offset=${offset}`),
  capture: (username, day, eventId, offset) => faceRequest(username, `/capture-image?day=${encodeURIComponent(day)}&event_id=${encodeURIComponent(eventId)}${offset == null ? '' : `&offset=${offset}`}`, {}, true),
}
